import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from 'constants/apiConfig';
import { selectGroupGuid } from 'slices/commonSelectors';
import { useGovernmentOfOntarioFlag } from 'utils/featureFlags';
import {
  ONTARIO_GTA_STORM_LAYER_DESCRIPTION,
  ONTARIO_GTA_STORM_LAYER_NAME,
  ONTARIO_GTA_STORM_POLYGON_COLOR,
  ONTARIO_GTA_STORM_POLYGON_OPACITY,
  ONTARIO_GTA_STORM_TRACK,
  ONTARIO_GTA_STORM_TRACK_DESCRIPTION,
  ONTARIO_GTA_STORM_TRACK_NAME,
  ONTARIO_GTA_STORM_WARNING_DESCRIPTION,
  ONTARIO_GTA_STORM_WARNING_NAME,
  ONTARIO_GTA_STORM_WARNING_RING,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/ontarioSnowstormLayerData';
import type { CreateIcsIncidentLocationBody } from 'hooks/icsLocation/icsIncidentLocation.types';
import { OIL_SPILL_RESOURCE } from 'hooks/icsLocation/icsIncidentLocation.types';
import { internalLayersKeys } from 'hooks/internalLayer/queryKeys';
import { layerNodesKeys } from 'hooks/layerNode/queryKeys';
import type {
  CreateInternalLayerBody,
  InternalLayer,
  ListInternalLayersFilter,
  ListLayersResponse,
} from 'hooks/internalLayer/types';
import { buildSearchQueryParams } from 'hooks/shared/buildSearchQueryParams';

const ensureLocks = new Map<string, Promise<unknown>>();

const STORM_LOCATIONS: CreateIcsIncidentLocationBody[] = [
  {
    name: ONTARIO_GTA_STORM_WARNING_NAME,
    description: ONTARIO_GTA_STORM_WARNING_DESCRIPTION,
    resourceType: 'customPolygon',
    geometry: {
      type: 'Polygon',
      coordinates: [ONTARIO_GTA_STORM_WARNING_RING],
    },
    properties: {
      polygonColor: ONTARIO_GTA_STORM_POLYGON_COLOR,
      polygonOpacity: ONTARIO_GTA_STORM_POLYGON_OPACITY,
    },
  },
  {
    name: ONTARIO_GTA_STORM_TRACK_NAME,
    description: ONTARIO_GTA_STORM_TRACK_DESCRIPTION,
    // LineStrings only draw for oilSpill / boom in syncInternalLayers.
    resourceType: OIL_SPILL_RESOURCE,
    geometry: {
      type: 'LineString',
      coordinates: ONTARIO_GTA_STORM_TRACK,
    },
    properties: {},
  },
];

async function findStormLayer(
  groupGuid: string
): Promise<InternalLayer | undefined> {
  const resp = await axios.get<ListLayersResponse<InternalLayer>>(
    config.apiGateway.internal_layers,
    {
      params: buildSearchQueryParams<ListInternalLayersFilter>({
        filter: { groupGuid, search: ONTARIO_GTA_STORM_LAYER_NAME },
        perPage: 50,
      }),
    }
  );
  return (resp.data?.data ?? []).find(
    (layer) => layer.name === ONTARIO_GTA_STORM_LAYER_NAME
  );
}

async function ensureOntarioGtaStormLayerForGroup({
  groupGuid,
  queryClient,
}: {
  groupGuid: string;
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<boolean> {
  const existing = await findStormLayer(groupGuid);
  if (existing) {
    if (existing.defaultVisible === true) return false;
    await axios.patch(`${config.apiGateway.internal_layers}/${existing.id}`, {
      defaultVisible: true,
    });
    await queryClient.invalidateQueries({
      queryKey: internalLayersKeys.all(),
    });
    return true;
  }

  const body: CreateInternalLayerBody = {
    groupGuid,
    incidentId: null,
    name: ONTARIO_GTA_STORM_LAYER_NAME,
    description: ONTARIO_GTA_STORM_LAYER_DESCRIPTION,
    defaultVisible: true,
    icsIncidentLocations: STORM_LOCATIONS,
  };
  await axios.post<InternalLayer>(config.apiGateway.internal_layers, body);
  await queryClient.invalidateQueries({ queryKey: internalLayersKeys.all() });
  await queryClient.invalidateQueries({ queryKey: layerNodesKeys.all() });
  return true;
}

/**
 * Seeds a hub-scoped internal layer with the synthetic GTA winter-storm
 * warning polygon and approach track when GOVERNMENT_OF_ONTARIO is on.
 */
export function useEnsureOntarioGtaStormLayer() {
  const ontarioEnabled = useGovernmentOfOntarioFlag();
  const groupGuid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  const completedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!ontarioEnabled || !groupGuid) {
      return;
    }
    if (completedRef.current === groupGuid) {
      return;
    }

    const lockKey = `ontario-storm-layer:${groupGuid}`;
    let cancelled = false;

    const run = async () => {
      const existing = ensureLocks.get(lockKey);
      if (existing) {
        await existing;
        if (!cancelled) completedRef.current = groupGuid;
        return;
      }

      const promise = ensureOntarioGtaStormLayerForGroup({
        groupGuid,
        queryClient,
      }).finally(() => {
        ensureLocks.delete(lockKey);
      });
      ensureLocks.set(lockKey, promise);

      try {
        await promise;
        if (!cancelled) completedRef.current = groupGuid;
      } catch (error) {
        console.error('Failed to ensure Ontario GTA storm layer', error);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [ontarioEnabled, groupGuid, queryClient]);
}
